import {HTMLAttributes, useEffect, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {getGqlErrorMessage} from '@/api/request';
import {useCheckUserExists} from '@/api/session';
import {SignInSteps} from '@/enums';
import {FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {useQueryClient} from '@tanstack/react-query';
import {Button} from '../Button';
import {TextField} from '../TextField';
import {Typography} from '../Typography';
import {InputContainer, TextFieldLabel} from './StyledComponents';

interface Inputs {
  email: string;
}

interface IProps extends HTMLAttributes<HTMLFormElement> {
  defaultValue?: string;
  onChanged(data: Inputs): void;
  onComplete(nextStep: SignInSteps): void;
}

export const EmailStep = ({
  defaultValue,
  onChanged,
  onComplete,
  ...props
}: IProps) => {
  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    setFocus,
    formState: {errors},
  } = useForm<Inputs>({mode: 'all'});
  const [email, setEmail] = useState<string | undefined>(defaultValue);

  const {
    data,
    isSuccess,
    isRefetching,
    error: queryError,
    refetch: checkValidation,
  } = useCheckUserExists(email);
  const queryClient = useQueryClient();

  const onSubmit: SubmitHandler<Inputs> = (data: Inputs) => {
    onChanged(data);
    checkValidation();
  };

  useEffect(() => {
    const subscription = watch((value) => setEmail(value.email));

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  useEffect(() => {
    if (queryError) {
      setError('email', {
        type: queryError?.name,
        message: getGqlErrorMessage(
          error,
          '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryError]);

  useEffect(() => {
    if (isSuccess && !!data && email) {
      onComplete(
        !!data.result ? SignInSteps.signinPassword : SignInSteps.signupPassword
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries({queryKey: ['session/user-exists']});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = errors['email']?.message;

  return (
    <InputContainer as="form" {...props} onSubmit={handleSubmit(onSubmit)}>
      <TextFieldLabel error={!!error?.length}>이메일</TextFieldLabel>
      <TextField
        {...register('email', {
          required: '이메일을 입력해 주세요.',
          pattern: {
            value: /^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/g,
            message: '올바른 이메일 형식을 입력해 주세요.',
          },
        })}
        fullWidth
        placeholder="예) mf@myfranchise.kr"
        variant="xlarge"
        value={email}
        error={error}
        onClear={reset}
      />
      <PaddingBox top={8}>
        <Typography
          color={palette.gray600}
          variant="medium12"
          lineHeight="20px"
        >
          마이프차 서비스 계정과 연동되지 않아 별도로 신규 회원가입을 해주셔야
          서비스를 이용하실 수 있습니다.
        </Typography>
      </PaddingBox>
      <FlexFullGrow />
      <Button
        type="submit"
        disabled={error || !email || isRefetching}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
      >
        {isRefetching ? '이메일 확인 중...' : '계속하기'}
      </Button>
    </InputContainer>
  );
};
